import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';

import {
  formatResliceSummary,
  reslicedLapKindCounts,
  type ResliceLapsResult,
} from '../../lib/circuitLapReslice';

export type CircuitLapResliceSettingsProps = {
  reslice: ResliceLapsResult;
  useFixedLaps: boolean;
  onUseFixedLapsChange: (value: boolean) => void;
  onRegenerate: () => void;
  onSave: () => void;
  onClearSaved?: () => void;
  saving?: boolean;
  hasDraft?: boolean;
  hasSaved?: boolean;
  savedAt?: string | null;
  circuitName?: string | null;
  circuitSourceLabel?: string | null;
  assignedContributionId?: string | null;
  resolvedContributionId?: string | null;
  canEdit?: boolean;
  saveError?: string | null;
  compact?: boolean;
  /** Hide the MyChron vs circuit timing switch (Sessions setup already has its own lap mode toggle). */
  hideTimingToggle?: boolean;
};

export function CircuitLapResliceSettings({
  reslice,
  useFixedLaps,
  onUseFixedLapsChange,
  onRegenerate,
  onSave,
  onClearSaved,
  saving = false,
  hasDraft = false,
  hasSaved = false,
  savedAt = null,
  circuitName = null,
  circuitSourceLabel = null,
  assignedContributionId = null,
  resolvedContributionId = null,
  canEdit = false,
  saveError = null,
  compact = false,
  hideTimingToggle = false,
}: CircuitLapResliceSettingsProps) {
  const counts = reslicedLapKindCounts(reslice);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: compact ? 0.5 : 0.75 }}>
        <Box>
          <Typography sx={{ fontSize: compact ? '0.68rem' : '0.72rem', fontWeight: 850, letterSpacing: '0.04em' }}>
            Circuit laps
          </Typography>
          {!compact ? (
            <Typography sx={{ fontSize: '0.58rem', color: '#94A3B8', lineHeight: 1.35 }}>
              Split at the saved circuit start line
            </Typography>
          ) : null}
        </Box>
        <Chip label={formatResliceSummary(counts)} size="small" sx={{ height: 22, fontSize: '0.62rem' }} />
      </Stack>

      {circuitName ? (
        <Box sx={{ mb: 0.85, px: 0.15 }}>
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 750, color: '#CBD5E1', lineHeight: 1.35 }}>
            {circuitName}
          </Typography>
          <Typography sx={{ fontSize: '0.56rem', color: '#64748B', lineHeight: 1.35 }}>
            {circuitSourceLabel ?? 'Circuit overlay'}
            {assignedContributionId
              ? assignedContributionId === resolvedContributionId
                ? ' · Saved on session'
                : ' · Preview only — assign below'
              : ' · Not assigned — pick a circuit below'}
          </Typography>
        </Box>
      ) : null}

      {hideTimingToggle ? null : (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.85 }}>
          <Box>
            <Typography sx={{ fontSize: '0.62rem', color: '#A6B0BE' }}>
              {useFixedLaps ? 'Circuit lap timing' : 'MyChron lap timing'}
            </Typography>
            <Typography sx={{ fontSize: '0.56rem', color: '#64748B', lineHeight: 1.35 }}>
              {useFixedLaps ? 'Laps split at the circuit start line' : 'Original logger lap times'}
            </Typography>
          </Box>
          <Switch
            size="small"
            checked={useFixedLaps}
            onChange={(event) => onUseFixedLapsChange(event.target.checked)}
            inputProps={{ 'aria-label': 'Toggle between circuit laps and MyChron laps' }}
          />
        </Stack>
      )}

      <Stack direction="row" flexWrap="wrap" gap={0.5} useFlexGap>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AutorenewIcon sx={{ fontSize: 14 }} />}
          onClick={onRegenerate}
          sx={{ flex: 1, minWidth: 0, fontSize: '0.62rem', textTransform: 'none', py: 0.35 }}
        >
          Regenerate
        </Button>
        {canEdit ? (
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<SaveOutlinedIcon sx={{ fontSize: 14 }} />}
            disabled={saving || !hasDraft}
            onClick={onSave}
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: '0.62rem',
              textTransform: 'none',
              py: 0.35,
              color: '#fff',
              '& .MuiButton-startIcon': { color: '#fff' },
            }}
          >
            {saving ? 'Saving…' : hasSaved ? 'Update laps' : 'Save laps'}
          </Button>
        ) : null}
      </Stack>

      {canEdit && hasSaved && onClearSaved ? (
        <Button
          size="small"
          variant="text"
          disabled={saving}
          onClick={onClearSaved}
          sx={{ mt: 0.5, px: 0, minWidth: 0, fontSize: '0.62rem', textTransform: 'none', color: 'text.secondary' }}
        >
          Clear saved laps
        </Button>
      ) : null}

      {saveError ? (
        <Alert severity="error" sx={{ mt: 0.75, py: 0.25 }}>
          {saveError}
        </Alert>
      ) : null}

      {savedAt ? (
        <Typography sx={{ fontSize: '0.58rem', color: '#94A3B8', mt: 0.75, lineHeight: 1.45 }}>
          Saved {new Date(savedAt).toLocaleString()}.
          {hideTimingToggle ? '' : ' Turn off the switch above to compare MyChron laps.'}
        </Typography>
      ) : hasDraft ? (
        <Typography sx={{ fontSize: '0.58rem', color: '#94A3B8', mt: 0.75, lineHeight: 1.45 }}>
          Unsaved regeneration — save to keep this lap set on the session.
        </Typography>
      ) : reslice.laps.length === 0 ? (
        <Typography sx={{ fontSize: '0.62rem', color: '#CBD5E1', mt: 0.75, lineHeight: 1.45 }}>
          No crossings detected at the circuit start line. Assign a circuit above, then regenerate.
        </Typography>
      ) : null}
    </Box>
  );
}
